import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Application, ApplicationAction } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { Config } from "../../model_gen/config";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { RequestsService } from "../../services/RequestsService";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import AiStars from "../../assets/svg/ai-creation.svg?react";

import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";
import { DatasetService } from "../../services/DatasetService";
import DatasetsTable from "../shared/DatasetsTable";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { CompositionService } from "../../services/CompositionService";
import { ModelService } from "../../services/ModelService";
import { Model } from "../../model_gen/model";
import ModelsTable from "../shared/ModelsTable";
import RequestsTable from "../shared/RequestsTable";
import CompositionsTable from "../shared/CompositionsTable";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import {
  AppWindowIcon,
  HistoryIcon,
  FileTextIcon,
  CalendarIcon,
  ServerIcon,
  SettingsIcon,
  SearchIcon,
  PlusIcon,
  RotateCcwIcon,
  Loader2Icon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import { ConfigurationPanelRef } from "../../types/IApplication";
import DateRangeFilter from "../shared/DateRangeFilter";
import RequestAnalyticsChart from "../shared/RequestAnalyticsChart";
import FaultAnalyticsChart from "../shared/FaultAnalyticsChart";
import {
  AnalyticsService,
  ApplicationAnalyticsResponse,
  FaultRateAnalyticsResponse,
  RequestAnalyticsData,
  FaultRateAnalyticsData,
} from "../../services/AnalyticsService";
import { QuickPeriod } from "../shared/AnalyticsDateFilter";

dayjs.extend(utc);

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const ApplicationActionOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId, actionId } = useParams<{
    applicationId: string;
    actionId: string;
  }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<Application | null>(null);
  const [config, setConfig] = useState<Config>();
  const [applicationAction, setApplicationAction] =
    useState<ApplicationAction | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [initialLoadDate] = useState<number>(Date.now());
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState<boolean>(false);
  const [datasetsTotal, setDatasetsTotal] = useState<number>(0);
  const [datasetsCurrentPage, setDatasetsCurrentPage] = useState<number>(1);
  const datasetsPageSize = 10;
  const [compositions, setCompositions] = useState<any[]>([]);
  const [compositionsLoading, setCompositionsLoading] =
    useState<boolean>(false);
  const [compositionsTotal, setCompositionsTotal] = useState<number>(0);
  const [compositionsCurrentPage, setCompositionsCurrentPage] =
    useState<number>(1);
  const compositionsPageSize = 10;
  const [datasetsSubTab, setDatasetsSubTab] = useState<string>("datasets");
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);
  const [modelsTotal, setModelsTotal] = useState<number>(0);
  const [modelsCurrentPage, setModelsCurrentPage] = useState<number>(1);
  const modelsPageSize = 10;

  // Configuration save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const configPanelRef = useRef<ConfigurationPanelRef>(null);

  // Analytics state
  const [requestAnalyticsData, setRequestAnalyticsData] = useState<
    RequestAnalyticsData[]
  >([]);
  const [requestAnalyticsLoading, setRequestAnalyticsLoading] =
    useState<boolean>(false);
  const [requestAnalyticsPeriod, setRequestAnalyticsPeriod] =
    useState<QuickPeriod>({ label: "1W", days: 7 });
  const [requestAnalyticsPeriodDays, setRequestAnalyticsPeriodDays] =
    useState<number>(7);

  const [faultAnalyticsData, setFaultAnalyticsData] = useState<
    FaultRateAnalyticsData[]
  >([]);
  const [faultAnalyticsLoading, setFaultAnalyticsLoading] =
    useState<boolean>(false);
  const [faultAnalyticsPeriod, setFaultAnalyticsPeriod] = useState<QuickPeriod>(
    { label: "1W", days: 7 }
  );
  const [faultAnalyticsPeriodDays, setFaultAnalyticsPeriodDays] =
    useState<number>(7);

  const getConfig = () => {
    ApplicationService.getApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey
    ).subscribe(
      (config: Config) => {
        setConfig(config);
        setOriginalConfig(config);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    setConfig((prevConfig: Config | undefined) => {
      if (!prevConfig) {
        return config;
      }
      return {
        ...prevConfig,
        ...config,
      };
    });
    if (!save) {
      return;
    }
    ApplicationService.setApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (config: Config) => {
        setConfig(config);
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const resetConfig = () => {
    setConfig(undefined);
    ApplicationService.resetApplicationActionConfig(
      Number(applicationId),
      Number(actionId),
      null,
      apiKey
    ).subscribe(
      () => {
        getConfig();
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const handleSaveStateChange = (hasUnsaved: boolean, saving: boolean) => {
    setHasUnsavedChanges(hasUnsaved);
    setIsSaving(saving);
  };

  const handleSaveChanges = () => {
    if (configPanelRef.current) {
      configPanelRef.current.saveChanges();
    }
  };

  const fetchRequests = (page: number) => {
    // Server-side pagination - RequestsService supports offset/limit
    const offset = (page - 1) * requestPageSize;
    setRequestsLoading(true);

    const start = startDate
      ? dayjs(startDate).startOf("day").utc().valueOf()
      : -1;
    const endTemp = endDate
      ? dayjs(endDate).add(1, "day").startOf("day").utc().valueOf()
      : initialLoadDate;
    const end = endTemp > initialLoadDate ? initialLoadDate : endTemp;

    RequestsService.getApplicationActionRequests(
      Number(applicationId),
      Number(actionId),
      start,
      end,
      offset,
      requestPageSize,
      setRequestsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        setRequests(data.items || []);
        setRequestsTotal(data.total || 0);
        setRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setRequestsLoading(false);
      }
    );
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const fetchDatasets = (page: number) => {
    if (!applicationId || !actionId) return;

    // Server-side pagination - DatasetService supports offset/limit
    const offset = (page - 1) * datasetsPageSize;

    DatasetService.getDatasetsByIntent(
      Number(applicationId),
      Number(actionId),
      offset,
      datasetsPageSize,
      setDatasetsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setDatasetsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
      }
    );
  };

  const fetchCompositions = (page: number) => {
    if (!applicationId || !actionId) return;

    // Server-side pagination - CompositionService supports offset/limit
    const offset = (page - 1) * compositionsPageSize;

    CompositionService.getCompositionsByApplicationIntent(
      Number(applicationId),
      Number(actionId),
      offset,
      compositionsPageSize,
      setCompositionsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setCompositions(response.items || []);
        setCompositionsTotal(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching compositions:", error);
      }
    );
  };

  const fetchModels = () => {
    if (!actionId) return;

    setModelsLoading(true);
    ModelService.getModelsByIntent(
      Number(actionId),
      setModelsLoading,
      apiKey
    ).subscribe(
      (data: Model[]) => {
        // Filter to only include finetuned models
        const finetunedModels = data.filter((model) => model.finetuned);

        // Set total count
        setModelsTotal(finetunedModels.length);

        // Client-side pagination since ModelService doesn't support server-side pagination
        const startIndex = (modelsCurrentPage - 1) * modelsPageSize;
        const endIndex = startIndex + modelsPageSize;
        const paginatedModels = finetunedModels.slice(startIndex, endIndex);

        setModels(paginatedModels);
        setModelsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setModels([]);
        setModelsTotal(0);
        setModelsLoading(false);
      }
    );
  };

  const handleDeleteDataset = (dataset: FinetuneDataset) => {
    DatasetService.deleteDataset(dataset.id, null, apiKey).subscribe(
      () => {
        fetchDatasets(datasetsCurrentPage); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting dataset:", error);
      }
    );
  };

  const handleDeleteComposition = (composition: any) => {
    CompositionService.deleteComposition(
      composition.id.toString(),
      null,
      apiKey
    ).subscribe(
      () => {
        fetchCompositions(compositionsCurrentPage); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting composition:", error);
      }
    );
  };

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          ApplicationService.getApplicationAction(
            Number(applicationId),
            Number(actionId),
            null,
            apiKey
          ).subscribe((action: ApplicationAction) => {
            setApplicationAction(action);
            getConfig();
          });
        },
        (error: any) => {
          console.error(error);
        }
      );
      if (applicationId && actionId) {
        IntentService.getIntentGroupByApplicationAndIntent(
          Number(applicationId),
          Number(actionId),
          () => {},
          apiKey
        ).subscribe(
          (intentGroup: IntentGroup) => {
            setIntentGroup(intentGroup);
          },
          (error: any) => {
            console.error(error);
          }
        );
      }
    }
  }, [applicationId, actionId]);

  useEffect(() => {
    if (applicationId && actionId && activeTab === "requests") {
      fetchRequests(requestsCurrentPage);
    }
  }, [
    applicationId,
    actionId,
    requestsCurrentPage,
    activeTab,
    requestPageSize,
  ]);

  useEffect(() => {
    if (
      (!startDate || startDate == "") &&
      (!endDate || endDate == "") &&
      activeTab === "requests"
    ) {
      setRequestsCurrentPage(1);
      fetchRequests(1);
    }
  }, [startDate, endDate, activeTab]);

  useEffect(() => {
    if (applicationId && actionId && activeTab === "datasets") {
      if (datasetsSubTab === "datasets") {
        fetchDatasets(datasetsCurrentPage);
      } else if (datasetsSubTab === "compositions") {
        fetchCompositions(compositionsCurrentPage);
      }
    }
  }, [
    applicationId,
    actionId,
    datasetsCurrentPage,
    compositionsCurrentPage,
    activeTab,
    datasetsSubTab,
  ]);

  useEffect(() => {
    if (applicationId && actionId && activeTab === "models") {
      fetchModels();
    }
  }, [applicationId, actionId, activeTab, modelsCurrentPage]);

  // Analytics fetch functions
  const fetchRequestAnalytics = useCallback(
    (period: QuickPeriod) => {
      if (!applicationId || !actionId) return;

      setRequestAnalyticsLoading(true);

      const now = moment();
      const days = period.days;

      let startDate: string;
      let endDate: string;

      if (days === 1) {
        // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
        startDate = now
          .clone()
          .subtract(24, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For multi-day periods, use the same logic as 1D but with more hours
        const totalHours = days * 24;
        startDate = now
          .clone()
          .subtract(totalHours, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      }

      console.log(
        `📅 Application Action Request analytics period: ${days} days, ${startDate} to ${endDate}`
      );
      setRequestAnalyticsPeriodDays(days);

      AnalyticsService.getApplicationActionRequestAnalytics(
        Number(applicationId),
        Number(actionId),
        setRequestAnalyticsLoading,
        apiKey,
        startDate,
        endDate
      ).subscribe(
        (response: ApplicationAnalyticsResponse) => {
          console.log(
            "📊 Application Action Request analytics response:",
            response
          );
          setRequestAnalyticsData(response.data || []);
        },
        (error: any) => {
          console.error(
            "Error fetching application action request analytics:",
            error
          );
          setRequestAnalyticsData([]);
        }
      );
    },
    [applicationId, actionId, apiKey]
  );

  const fetchFaultAnalytics = useCallback(
    (period: QuickPeriod) => {
      if (!applicationId || !actionId) return;

      setFaultAnalyticsLoading(true);

      const now = moment();
      const days = period.days;

      let startDate: string;
      let endDate: string;

      if (days === 1) {
        // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
        startDate = now
          .clone()
          .subtract(24, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For multi-day periods, use the same logic as 1D but with more hours
        const totalHours = days * 24;
        startDate = now
          .clone()
          .subtract(totalHours, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
      }

      console.log(
        `📅 Application Action Fault analytics period: ${days} days, ${startDate} to ${endDate}`
      );
      setFaultAnalyticsPeriodDays(days);

      AnalyticsService.getApplicationActionFaultRateAnalytics(
        Number(applicationId),
        Number(actionId),
        setFaultAnalyticsLoading,
        apiKey,
        startDate,
        endDate
      ).subscribe(
        (response: FaultRateAnalyticsResponse) => {
          console.log(
            "📊 Application Action Fault analytics response:",
            response
          );
          setFaultAnalyticsData(response.data || []);
        },
        (error: any) => {
          console.error(
            "Error fetching application action fault analytics:",
            error
          );
          setFaultAnalyticsData([]);
        }
      );
    },
    [applicationId, actionId, apiKey]
  );

  const handleRequestAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setRequestAnalyticsPeriod(period);
    fetchRequestAnalytics(period);
  };

  const handleFaultAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setFaultAnalyticsPeriod(period);
    fetchFaultAnalytics(period);
  };

  // Load request analytics when overview tab becomes active
  useEffect(() => {
    if (activeTab === "overview") {
      fetchRequestAnalytics(requestAnalyticsPeriod);
    }
  }, [activeTab, fetchRequestAnalytics]);

  // Load fault analytics when overview tab becomes active
  useEffect(() => {
    if (activeTab === "overview") {
      fetchFaultAnalytics(faultAnalyticsPeriod);
    }
  }, [activeTab, fetchFaultAnalytics]);

  // Parse tags from metadata
  const getTags = () => {
    try {
      return JSON.parse(
        (applicationAction?.meta?.metadata?.tags as string) || "[]"
      );
    } catch (e) {
      return [];
    }
  };

  // Parse description from metadata
  const getDescription = () => {
    try {
      return (applicationAction?.meta?.metadata?.intent as string) || "-";
    } catch (e) {
      return "-";
    }
  };

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AppWindowIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Intent Group
                    </p>
                  </div>
                  {intentGroup ? (
                    <button
                      onClick={() =>
                        navigate(`/intent-group/${intentGroup.id}`)
                      }
                      className="text-lg font-gc-med text-maitai-green hover:underline text-left truncate w-full"
                      title={intentGroup.intent_name}>
                      {intentGroup.intent_name}
                    </button>
                  ) : (
                    <p className="text-lg font-gc-med text-foreground">-</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Total Requests
                    </p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {applicationAction?.request_count || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <HistoryIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Last Activity
                    </p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {applicationAction?.last_activity
                      ? moment(applicationAction?.last_activity).fromNow()
                      : "Never"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">
                      Created
                    </p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {applicationAction?.date_created
                      ? moment(applicationAction.date_created).format(
                          "MMM D, YYYY"
                        )
                      : "-"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RequestAnalyticsChart
              data={requestAnalyticsData}
              loading={requestAnalyticsLoading}
              title="Request Activity"
              onQuickPeriodSelect={handleRequestAnalyticsPeriodSelect}
              selectedPeriod={requestAnalyticsPeriod}
              periodDays={requestAnalyticsPeriodDays}
            />
            <FaultAnalyticsChart
              data={faultAnalyticsData}
              loading={faultAnalyticsLoading}
              title="Fault Rate"
              onQuickPeriodSelect={handleFaultAnalyticsPeriodSelect}
              selectedPeriod={faultAnalyticsPeriod}
              periodDays={faultAnalyticsPeriodDays}
            />
          </div>
        </div>
      ),
    },
    {
      key: "requests",
      label: "Requests",
      icon: <SearchIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Requests</h3>
              <p className="text-sm text-muted-foreground">
                View and filter request history
              </p>
            </div>
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onClearFilters={handleClearFilters}
              onSearch={() => {
                setRequestsCurrentPage(1);
                fetchRequests(1);
              }}
              isLoading={requestsLoading}
            />
          </div>

          <RequestsTable
            entityType="applicationAction"
            entityId={Number(actionId)}
            applicationId={Number(applicationId)}
            requests={requests}
            requestsTotal={requestsTotal}
            currentPage={requestsCurrentPage}
            pageSize={requestPageSize}
            loading={requestsLoading}
            onPageChange={setRequestsCurrentPage}
            onFetchRequests={fetchRequests}
            startDate={startDate}
            endDate={endDate}
            apiKey={apiKey}
            showApplicationColumn={false}
          />
        </div>
      ),
    },
    {
      key: "datasets",
      label: "Datasets",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Datasets & Compositions
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage training datasets and model compositions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() =>
                  navigate(
                    `/forge/compositions/new?step=1&base_type=application_intent&application_id=${applicationId}&intent_id=${actionId}`
                  )
                }
                className="gap-2">
                <PlusIcon className="w-4 h-4" />
                New Composition
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    `/forge/datasets/new?step=1&base_type=application_intent&application_id=${applicationId}&intent_id=${actionId}`
                  )
                }
                className="gap-2">
                <PlusIcon className="w-4 h-4" />
                New Dataset
              </Button>
            </div>
          </div>

          {/* Sub-tabs for datasets and compositions */}
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              <button
                onClick={() => setDatasetsSubTab("datasets")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  datasetsSubTab === "datasets"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Datasets
              </button>
              <button
                onClick={() => setDatasetsSubTab("compositions")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  datasetsSubTab === "compositions"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Compositions
              </button>
            </nav>
          </div>

          {datasetsSubTab === "datasets" ? (
            <DatasetsTable
              datasets={datasets}
              loading={datasetsLoading}
              currentPage={datasetsCurrentPage}
              totalPages={Math.ceil(datasetsTotal / datasetsPageSize)}
              totalItems={datasetsTotal}
              itemsPerPage={datasetsPageSize}
              onPageChange={setDatasetsCurrentPage}
              onDeleteDataset={handleDeleteDataset}
            />
          ) : (
            <CompositionsTable
              compositions={compositions}
              loading={compositionsLoading}
              currentPage={compositionsCurrentPage}
              totalPages={Math.ceil(compositionsTotal / compositionsPageSize)}
              totalItems={compositionsTotal}
              itemsPerPage={compositionsPageSize}
              onPageChange={setCompositionsCurrentPage}
              onDeleteComposition={handleDeleteComposition}
            />
          )}
        </div>
      ),
    },
    {
      key: "models",
      label: "Models",
      icon: <ServerIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Models</h3>
            <p className="text-sm text-muted-foreground">
              Manage models associated with this intent
            </p>
          </div>

          <ModelsTable
            models={models}
            loading={modelsLoading}
            currentPage={modelsCurrentPage}
            totalPages={Math.ceil(modelsTotal / modelsPageSize)}
            totalItems={modelsTotal}
            itemsPerPage={modelsPageSize}
            onPageChange={setModelsCurrentPage}
          />
        </div>
      ),
    },
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Configuration
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage intent settings and parameters
              </p>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
              )}
              <Button variant="outline" onClick={resetConfig} className="gap-2">
                <RotateCcwIcon className="w-4 h-4" />
                Reset to Default
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || isSaving}
                className="gap-2">
                {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
          <ConfigurationPanel
            ref={configPanelRef}
            intentGroupId={intentGroup?.id}
            config={config}
            updateConfig={updateConfig}
            resetConfig={resetConfig}
            showSystemPrompt={false}
            onSaveStateChange={handleSaveStateChange}
          />
        </div>
      ),
    },
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/applications")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Applications
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate(`/application/${applicationId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    {application?.application_name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {applicationAction?.action_type}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-3">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                {applicationAction?.action_type}
              </h1>
              <div className="space-y-3">
                {/* Description with AI icon */}
                <div className="flex items-start gap-1">
                  <p className="text-lg text-muted-foreground font-gc-reg">
                    {getDescription()}
                  </p>
                  <AiStars className="w-3 h-3 text-muted-foreground mt-1 flex-shrink-0" />
                </div>
                {/* Tags with AI icon */}
                <div className="flex items-start gap-1">
                  <div className="flex flex-wrap gap-2">
                    {getTags().map((tag: string) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border border-maitai-green text-maitai-green bg-green-50 dark:bg-green-950/20">
                        {tag}
                      </span>
                    ))}
                    {getTags().length === 0 && (
                      <span className="text-sm text-muted-foreground">
                        No tags available
                      </span>
                    )}
                  </div>
                  <AiStars className="w-3 h-3 text-muted-foreground mt-1 flex-shrink-0" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find((tab) => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationActionOverview;
